import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IonPage, useIonViewDidEnter, useIonViewDidLeave } from '@ionic/react';
import {
  AppFooter_v2 as AppFooter,
  Button_v2 as Button,
  Container_v2 as Container,
  ScreenInput,
  Typography,
  TypographyVariantsEnum,
} from '@mindly/ui-components';
import { RouteComponentProps } from 'react-router';
import { useTranslation } from 'react-i18next';
import { PublicRoutes } from '../../../routes/navigation';
import { useIsKeyBoardShown, useAnalytics } from '../../../shared';
import { useDispatch, useSelector } from 'react-redux';
import { authActionSelector, isWebToWebAuthSelector, signUpEmailSelector } from '../../../redux/auth/selector';
import { Link } from 'react-router-dom';
import { AuthPageHeaderFeature } from 'src/features/AuthPageHeaderFeature';
import { setTimerData } from 'src/redux/promoCodes/promoCodes.reducer';
import { useAuthFlow } from 'src/shared/hooks/useAuthFlow';
import useToast from 'src/context/ToastContext/ToastContext';

const PasswordPage: FC<RouteComponentProps> = ({ history }) => {
  const { t } = useTranslation('auth');
  const {
    triggerSignInWithEmailAndPasswordFlow,
    triggerLinkWithEmailAndPasswordFlow,
    triggerCreateUserWithEmailAndPasswordFlow,
  } = useAuthFlow();
  const isKeyboardShown = useIsKeyBoardShown();
  const { trackPredefinedEvent } = useAnalytics();
  const { showToast } = useToast();

  const passwordRef = useRef<HTMLInputElement | null>(null);

  const signUpEmail = useSelector(signUpEmailSelector);
  const authAction = useSelector(authActionSelector);
  const isWebToWebAuth = useSelector(isWebToWebAuthSelector);

  const [password, setPassword] = React.useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const dispatch = useDispatch();

  useIonViewDidEnter(() => {
    if (!signUpEmail || !authAction) {
      const webToWebAuth = localStorage.getItem('webToWebAuth');

      if (webToWebAuth) {
        history.push(PublicRoutes.AUTH);
      } else {
        history.push(PublicRoutes.EMAIL_SIGN_IN);
      }
      return;
    }
  }, [authAction, signUpEmail]);

  useIonViewDidEnter(() => {
    if (authAction && authAction !== 'signIn') {
      trackPredefinedEvent('SIGNUP_USER_INFO_VIEWED', {
        screen_type: 'password',
      });
    }
  });

  useIonViewDidEnter(() => {
    if (passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [passwordRef]);

  useIonViewDidLeave(() => {
    setPassword('');
  });

  useEffect(() => {
    setIsDisabled(password.length <= 5);
  }, [password]);

  const handleSubmit = useCallback(async () => {
    if (isDisabled) return;

    try {
      setIsLoading(true);
      switch (authAction) {
        case 'signIn':
          await triggerSignInWithEmailAndPasswordFlow({
            email: signUpEmail,
            password,
          });
          break;
        case 'signUp':
          trackPredefinedEvent('SIGNUP_USER_INFO_SUBMITTED', {
            screen_type: 'password',
          });
          await triggerCreateUserWithEmailAndPasswordFlow({
            email: signUpEmail,
            password,
          });
          break;
        case 'link':
          trackPredefinedEvent('SIGNUP_USER_INFO_SUBMITTED', {
            screen_type: 'password',
          });
          await triggerLinkWithEmailAndPasswordFlow({
            email: signUpEmail,
            password,
          });
          break;
      }
      dispatch(setTimerData(undefined));
    } catch (error) {
      showToast({ error });
    } finally {
      setIsLoading(false);
    }
  }, [authAction, signUpEmail, password, isDisabled, trackPredefinedEvent]);

  const redirectTo = useMemo(() => {
    if (authAction && ['link', 'signIn'].includes(authAction)) {
      if (isWebToWebAuth || localStorage.getItem('webToWebAuth')) {
        return PublicRoutes.AUTH;
      } else {
        return PublicRoutes.EMAIL_SIGN_IN;
      }
    } else {
      return PublicRoutes.COUNTRY_SIGN_UP;
    }
  }, [authAction, isWebToWebAuth]);

  return (
    <IonPage>
      <AuthPageHeaderFeature backButtonLink={redirectTo} />
      <Container isFlexContainer>
        {authAction === 'signIn' ? (
          <>
            <Typography variant={TypographyVariantsEnum.Title28Semi} className={'mb-1'}>
              {t('auth.enter.password.title')}
            </Typography>
            <Typography variant={TypographyVariantsEnum.Text15Regular} opacity={50}>
              {t('auth.enter.password.description', {
                client_email: signUpEmail,
              })}
            </Typography>
            <Link to={PublicRoutes.FORGOT_PASSWORD}>
              <Typography variant={TypographyVariantsEnum.Text15Regular} opacity={50} className={'underline'}>
                {t('auth.enter.password.forgot.password.link')}
              </Typography>
            </Link>
          </>
        ) : (
          <>
            <Typography variant={TypographyVariantsEnum.Title28Semi} className={'mb-1'}>
              {t('auth.create.password.title')}
            </Typography>
            <Typography variant={TypographyVariantsEnum.Text15Regular} opacity={50}>
              {t('auth.create.password.description')}
            </Typography>
          </>
        )}
        <ScreenInput
          ref={passwordRef}
          id={'password'}
          type={'password'}
          placeholder={'••••••••'}
          value={password}
          autoComplete={'current-password'}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
        />
      </Container>
      <AppFooter transparent bottomSaveArea={!isKeyboardShown}>
        <Button isLoading={isLoading} disabled={isDisabled} fullWidth size={'large'} onClick={handleSubmit}>
          {authAction === 'signIn' ? t('auth.enter.password.button') : t('auth.button.next')}
        </Button>
      </AppFooter>
    </IonPage>
  );
};

export default PasswordPage;
